import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { TInsightsOnTags } from "../../../domain/entities/insights-on-tags";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";
import { checkParamsFromCreative } from "helper/filtersHelper";

type TInsightsOnTagsProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
  showCards?: boolean;
};

export type TInsightsOnTagsState = {
  requestParamsIndex: number;
  downloadingReports?: boolean;
  insightsOnTags?: TInsightsOnTags;
  downloadInsightsOnTagsCSV: () => void;
  isLoading: boolean;
};

const initContext: TInsightsOnTagsState = {
  requestParamsIndex: 0,
  downloadInsightsOnTagsCSV: () => null,
  isLoading: true,
};
export const InsightsOnTagsContext = createContext(initContext);

export const InsightsOnTagsProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
  showCards = true,
}: TInsightsOnTagsProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [insightsOnTags, setInsightsOnTags] = useState<TInsightsOnTags>();
  const [downloadingReports, setDownloadingReports] = useState<boolean>(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadInsightsOnTagsCSV = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportInsightsOnTagsCSV(currentRequestParams)
      .then(createBlobDownloader("tags-insights.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromCreative);
    }

    return checkParamsFromCreative(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getInsightsOnTags({ ...currentRequestParams, showCards })
        .then(setInsightsOnTags)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <InsightsOnTagsContext.Provider
      value={{
        requestParamsIndex,
        insightsOnTags,
        downloadingReports,
        downloadInsightsOnTagsCSV,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </InsightsOnTagsContext.Provider>
  );
};
